import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { IoAdd, IoClose, IoSaveOutline, IoTrashOutline } from "react-icons/io5";
import CategorySelect from "./CategorySelect";
import { MainCategory } from "../data/categories";
import GearButton from "../ui/gearbutton";
import {
  updateGearItem,
  addGearItemImage,
  deleteGearItemImage,
  updateGearItemImage,
} from "../backend/api/gearlpal";
import GearInput from "../ui/gearinput";
import { GearItem, GearItemImage } from "../types/Pal";
import { convertToWebP } from "../utils/imageUtils";

interface EditGearItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: GearItem;
}

export default function EditGearItemModal({
  isOpen,
  onClose,
  item,
}: EditGearItemModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);

  const [editItem, setEditItem] = useState<{
    name: string;
    categoryId: string;
    categoryLabel: string;
    externalLink: string;
    description: string;
    noleggio: number;
  }>({
    name: item.name,
    categoryId: item.categoryId.toString(),
    categoryLabel: `${item.category} > ${item.subcategory} > ${item.label}`,
    externalLink: item.externalLink || "",
    description: "",
    noleggio: parseFloat(item.noleggio) || 0,
  });

  useEffect(() => {
    if (item) {
      setEditItem({
        name: item.name,
        categoryId: item.categoryId.toString(),
        categoryLabel: `${item.category} > ${item.subcategory} > ${item.label}`,
        externalLink: item.externalLink || "",
        description: "",
        noleggio: parseFloat(item.noleggio) || 0,
      });
      setImagesToDelete([]);
      setSelectedFiles([]);
    }
  }, [item]);

  const handleUpdate = async () => {
    if (!editItem.name || !editItem.categoryId || !item.id) return;

    try {
      setIsProcessing(true);

      // 1. Update Metadata
      await updateGearItem(
        item.id,
        {
          name: editItem.name,
          categoryId: editItem.categoryId,
          externalLink: editItem.externalLink,
          noleggio: String(editItem.noleggio),
        },
        i18n.language,
      );

      // 2. Delete images
      for (const imageId of imagesToDelete) {
        await deleteGearItemImage(imageId, i18n.language);
      }

      // 3. Add new images
      if (selectedFiles.length > 0) {
        const convertedFiles = await Promise.all(
          selectedFiles.map((file) => convertToWebP(file)),
        );

        let currentMaxOrder =
          item.images?.reduce((max, img) => Math.max(max, img.order), 0) || 0;

        for (const file of convertedFiles) {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("order", String(++currentMaxOrder));
          await addGearItemImage(item.id, formData, i18n.language);
        }
      }

      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      toast.success(t("item.updated", "Articolo aggiornato con successo!"));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t("errors.unknownError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles((prev) => [
        ...prev,
        ...Array.from(e.target.files || []),
      ]);
    }
  };

  const markImageForDeletion = (imageId: number) => {
    setImagesToDelete((prev) => [...prev, imageId]);
  };

  const unmarkImageForDeletion = (imageId: number) => {
    setImagesToDelete((prev) => prev.filter((id) => id !== imageId));
  };

  if (!isOpen) return null;

  const existingImages = (item.images || []).filter(
    (img) => !imagesToDelete.includes(img.id),
  );

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[2000]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#2D1B4E] border border-[#6E11B04D] rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 md:p-8 text-white relative custom-scrollbar">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <IoClose size={24} />
        </button>

        <h2 className="text-xl font-bold mb-6">
          {t("forms.editItem", "Modifica Articolo")}
        </h2>

        <div className="space-y-4">
          <GearInput
            label={t("forms.itemNameLabel", "Nome articolo")}
            value={editItem.name}
            onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
          />

          <CategorySelect
            value={
              editItem.categoryLabel
                ? {
                    main: editItem.categoryLabel.split(
                      " > ",
                    )[0] as MainCategory,
                    sub: editItem.categoryLabel.split(" > ")[1],
                    type: editItem.categoryLabel.split(" > ")[2],
                  }
                : null
            }
            onChange={(val) => {
              if (val) {
                setEditItem((prev) => ({
                  ...prev,
                  categoryId: val.type,
                  categoryLabel: `${val.main} > ${val.sub} > ${val.type}`,
                }));
              }
            }}
            useTranslationKeys={true}
            useBackendCategories={true}
          />

          <div className="flex gap-4">
            <GearInput
              label={t("forms.rentalCost", "Costo noleggio")}
              type="number"
              value={editItem.noleggio}
              onChange={(e) =>
                setEditItem({
                  ...editItem,
                  noleggio: parseFloat(e.target.value),
                })
              }
            />
            <GearInput
              label={t("forms.externalLinkLabel", "Link esterno")}
              value={editItem.externalLink}
              onChange={(e) =>
                setEditItem({ ...editItem, externalLink: e.target.value })
              }
            />
          </div>

          {/* Existing Images */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-300">
              {t("forms.existingImages", "Immagini esistenti")}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {item.images?.map((img) => (
                <div
                  key={img.id}
                  className={`relative aspect-square rounded-lg overflow-hidden border ${
                    imagesToDelete.includes(img.id)
                      ? "opacity-30 border-red-500"
                      : "border-white/10"
                  }`}
                >
                  <img
                    src={img.linkImage}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {imagesToDelete.includes(img.id) ? (
                    <button
                      onClick={() => unmarkImageForDeletion(img.id)}
                      className="absolute inset-0 bg-red-900/40 flex items-center justify-center text-white"
                    >
                      {t("forms.restore", "RIPRISTINA")}
                    </button>
                  ) : (
                    <button
                      onClick={() => markImageForDeletion(img.id)}
                      className="absolute top-1 right-1 bg-black/60 p-1.5 rounded-full text-white hover:text-red-500"
                    >
                      <IoTrashOutline size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-2 text-gray-300">
              {t("forms.addImages", "Aggiungi nuove immagini")}
            </label>
            <input
              type="file"
              multiple
              onChange={handleImageChange}
              className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#3A1A60] file:text-white hover:file:bg-[#4A2A70]"
            />

            {selectedFiles.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                {selectedFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="relative aspect-square rounded-lg overflow-hidden border border-secondary/30"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() =>
                        setSelectedFiles((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                      className="absolute top-1 right-1 bg-black/60 p-1.5 rounded-full text-white"
                    >
                      <IoClose size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-6">
            <GearButton
              variant="secondary"
              onClick={handleUpdate}
              disabled={isProcessing}
              className="w-full !rounded-full shadow-lg shadow-secondary/20"
            >
              <div className="flex items-center gap-2 justify-center">
                <IoSaveOutline size={20} />
                <span>
                  {isProcessing ? t("common.saving") : t("common.saveChanges")}
                </span>
              </div>
            </GearButton>
          </div>
        </div>
      </div>
    </div>
  );
}
